import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../components/modals/ConfirmModal";
import SBINModal from "../components/modals/SBINModal";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import { isSalonOwnerOrManager } from "../utils/salonRoles";
import { formatInTimeZone } from "../utils/datetime";
import { listStaff } from "../api/staffs";
import {
  completeMaintenanceTask,
  createComplianceReference,
  createMaintenanceTask,
  deactivateMaintenanceTask,
  deleteComplianceReference,
  getComplianceReferences,
  getMaintenanceTaskLogs,
  getMaintenanceTasks,
  seedMaintenanceDefaults,
  updateComplianceReference,
  updateMaintenanceTask,
} from "../api/maintenance";

const FREQUENCY_OPTIONS = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "BIWEEKLY", label: "Biweekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "CUSTOM_DAYS", label: "Custom Days" },
  { value: "AFTER_EACH_CLIENT", label: "After Each Client" },
  { value: "OTHER", label: "Other" },
];

const STATUS_LABELS = {
  RED: "Overdue",
  YELLOW: "Due Soon",
  GREEN: "On Track",
};

const STATUS_BADGE_STYLES = {
  RED: "border-rose-200 bg-rose-50 text-rose-700",
  YELLOW: "border-amber-200 bg-amber-50 text-amber-700",
  GREEN: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

const HELP_SECTIONS = [
  {
    id: "overview",
    title: "Overview",
    description: "Track and verify cleaning and maintenance routines.",
    bullets: [
      "Create tasks with frequency and assignment.",
      "Staff can mark assigned tasks done.",
      "Status colors are computed from frequency and completion history.",
    ],
  },
  {
    id: "compliance",
    title: "Compliance References",
    description: "Store jurisdiction-specific rules and links in one place.",
    bullets: [
      "Add links/notes from local health authority or state board.",
      "Rules vary by jurisdiction and should be confirmed locally.",
    ],
  },
];

const TROUBLESHOOTING_ITEMS = [
  {
    title: "Mark Done unavailable",
    body: "Only assigned staff or managers can complete a task.",
  },
  {
    title: "No references yet",
    body: "Open Compliance References and add your official links/notes.",
  },
];

const TOOLTIPS = {
  status: "Green: on track, Yellow: due soon, Red: overdue or not yet completed.",
  frequency: "Frequency determines when a task becomes due again.",
  compliance:
    "Rules vary by jurisdiction. Confirm requirements with your local health authority/state board.",
};

const EMPTY_TASK_FORM = {
  title: "",
  description: "",
  category: "",
  frequency_type: "DAILY",
  frequency_days: "",
  assigned_staff_id: "",
  is_active: true,
};

const EMPTY_REFERENCE_FORM = {
  title: "",
  url: "",
  note: "",
};

const normalizeText = (value) => {
  const cleaned = String(value || "").trim();
  return cleaned || null;
};

const frequencyLabel = (task) => {
  const key = String(task?.frequency_type || "").toUpperCase();
  if (key === "CUSTOM_DAYS") {
    const days = Number(task?.frequency_days || 0);
    return days > 0 ? `Every ${days} day${days === 1 ? "" : "s"}` : "Custom";
  }
  return FREQUENCY_OPTIONS.find((item) => item.value === key)?.label || "Other";
};

const displayDateTime = (localValue, utcValue, timeZone) => {
  if (localValue) return localValue;
  if (!utcValue) return "--";
  return (
    formatInTimeZone(
      utcValue,
      timeZone,
      {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "numeric",
        minute: "2-digit",
      },
      "en-US"
    ) || "--"
  );
};

export default function MaintenanceCleaning() {
  const navigate = useNavigate();
  const { user, token, role } = useAuth();
  const fallbackCanManage =
    isSalonOwnerOrManager(role) ||
    ["admin", "host"].includes((role || "").toLowerCase()) ||
    Boolean(user?.is_super_admin);

  const [tasks, setTasks] = useState([]);
  const [tasksError, setTasksError] = useState("");
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [canManageApi, setCanManageApi] = useState(null);
  const [salonTimezone, setSalonTimezone] = useState(
    user?.salon_timezone || "America/Toronto"
  );
  const [staffOptions, setStaffOptions] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    assigned_staff_id: "",
    status_color: "",
    frequency_type: "",
    category: "",
    show_inactive: false,
  });

  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK_FORM);
  const [taskError, setTaskError] = useState("");
  const [taskSaving, setTaskSaving] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const [completeTarget, setCompleteTarget] = useState(null);
  const [completeNote, setCompleteNote] = useState("");
  const [completeError, setCompleteError] = useState("");
  const [completeSaving, setCompleteSaving] = useState(false);

  const [logsOpen, setLogsOpen] = useState(false);
  const [logsTask, setLogsTask] = useState(null);
  const [logsData, setLogsData] = useState({
    logs: [],
    page: 1,
    page_size: 20,
    total: 0,
  });
  const [logsLoading, setLogsLoading] = useState(false);

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateSaving, setDeactivateSaving] = useState(false);

  const [complianceOpen, setComplianceOpen] = useState(false);
  const [complianceRefs, setComplianceRefs] = useState([]);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceError, setComplianceError] = useState("");
  const [referenceForm, setReferenceForm] = useState(EMPTY_REFERENCE_FORM);
  const [referenceEditId, setReferenceEditId] = useState(null);
  const [referenceSaving, setReferenceSaving] = useState(false);
  const [referenceDeleteTarget, setReferenceDeleteTarget] = useState(null);
  const [referenceDeleteSaving, setReferenceDeleteSaving] = useState(false);

  const [toastMessage, setToastMessage] = useState("");
  const toastRef = useRef(null);

  const canManage = canManageApi == null ? fallbackCanManage : Boolean(canManageApi);
  const isStaffScopedView = !canManage;

  const categoryOptions = useMemo(() => {
    const set = new Set([
      "Sanitation",
      "Equipment",
      "Laundry",
      "Floors",
      "Restroom",
      "Pedicure",
    ]);
    tasks.forEach((task) => task?.category && set.add(task.category));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tasks]);

  const showToast = useCallback((message) => {
    setToastMessage(message);
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => {
      setToastMessage("");
      toastRef.current = null;
    }, 3200);
  }, []);

  useEffect(
    () => () => {
      if (toastRef.current) clearTimeout(toastRef.current);
    },
    []
  );

  const loadTasks = useCallback(async () => {
    setLoadingTasks(true);
    setTasksError("");
    try {
      const params = {
        include_inactive: Boolean(filters.show_inactive),
        seed_defaults: true,
      };
      if (filters.search.trim()) params.search = filters.search.trim();
      if (!isStaffScopedView && filters.assigned_staff_id) {
        params.assigned_staff_id = Number(filters.assigned_staff_id);
      }
      if (filters.status_color) params.status_color = filters.status_color;
      if (filters.frequency_type) params.frequency_type = filters.frequency_type;
      if (filters.category) params.category = filters.category;

      const data = await getMaintenanceTasks(params, token);
      setTasks(Array.isArray(data?.tasks) ? data.tasks : []);
      setCanManageApi(Boolean(data?.can_manage));
      if (data?.salon_timezone) setSalonTimezone(data.salon_timezone);
    } catch (err) {
      setTasks([]);
      setTasksError(
        err?.response?.data?.detail || "Unable to load maintenance tasks."
      );
    } finally {
      setLoadingTasks(false);
    }
  }, [filters, token, isStaffScopedView]);

  const loadStaff = useCallback(async () => {
    if (!canManage) {
      setStaffOptions([]);
      return;
    }
    try {
      const rows = await listStaff({ is_active: true }, token);
      setStaffOptions(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setStaffOptions([]);
    }
  }, [token, canManage]);

  const loadCompliance = useCallback(async () => {
    setComplianceLoading(true);
    setComplianceError("");
    try {
      const data = await getComplianceReferences(token);
      setComplianceRefs(Array.isArray(data?.references) ? data.references : []);
    } catch (err) {
      setComplianceRefs([]);
      setComplianceError(
        err?.response?.data?.detail || "Unable to load compliance references."
      );
    } finally {
      setComplianceLoading(false);
    }
  }, [token]);

  const loadLogs = useCallback(
    async (taskId, page = 1, pageSize = 20) => {
      if (!taskId) return;
      setLogsLoading(true);
      try {
        const data = await getMaintenanceTaskLogs(
          taskId,
          { page, page_size: pageSize },
          token
        );
        setLogsData({
          logs: Array.isArray(data?.logs) ? data.logs : [],
          page: Number(data?.page || page),
          page_size: Number(data?.page_size || pageSize),
          total: Number(data?.total || 0),
        });
      } catch (err) {
        setLogsData({ logs: [], page, page_size: pageSize, total: 0 });
      } finally {
        setLogsLoading(false);
      }
    },
    [token]
  );

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    if (!canManage && filters.assigned_staff_id) {
      setFilters((prev) => ({ ...prev, assigned_staff_id: "" }));
    }
  }, [canManage, filters.assigned_staff_id]);

  useEffect(() => {
    if (complianceOpen) loadCompliance();
  }, [complianceOpen, loadCompliance]);

  useEffect(() => {
    if (logsOpen && logsTask?.id) {
      loadLogs(logsTask.id, logsData.page, logsData.page_size);
    }
  }, [logsOpen, logsTask, logsData.page, logsData.page_size, loadLogs]);
  const openTaskModalForCreate = () => {
    setEditingTask(null);
    setTaskForm(EMPTY_TASK_FORM);
    setTaskError("");
    setTaskModalOpen(true);
  };

  const openTaskModalForEdit = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task?.title || "",
      description: task?.description || "",
      category: task?.category || "",
      frequency_type: task?.frequency_type || "DAILY",
      frequency_days:
        task?.frequency_type === "CUSTOM_DAYS" && task?.frequency_days
          ? String(task.frequency_days)
          : "",
      assigned_staff_id: task?.assigned_staff_id ? String(task.assigned_staff_id) : "",
      is_active: Boolean(task?.is_active),
    });
    setTaskError("");
    setTaskModalOpen(true);
  };

  const saveTask = async () => {
    if (!canManage) return;
    const title = String(taskForm.title || "").trim();
    if (!title) {
      setTaskError("Task title is required.");
      return;
    }
    if (!taskForm.frequency_type) {
      setTaskError("Frequency is required.");
      return;
    }
    const payload = {
      title,
      description: normalizeText(taskForm.description),
      category: normalizeText(taskForm.category),
      frequency_type: taskForm.frequency_type,
      frequency_days: null,
      assigned_staff_id: taskForm.assigned_staff_id ? Number(taskForm.assigned_staff_id) : null,
      is_active: Boolean(taskForm.is_active),
    };
    if (taskForm.frequency_type === "CUSTOM_DAYS") {
      const days = Number(taskForm.frequency_days);
      if (!Number.isFinite(days) || days <= 0) {
        setTaskError("Custom day frequency must be greater than 0.");
        return;
      }
      payload.frequency_days = Math.floor(days);
    }

    setTaskSaving(true);
    setTaskError("");
    try {
      if (editingTask?.id) {
        await updateMaintenanceTask(editingTask.id, payload, token);
        showToast("Task updated.");
      } else {
        await createMaintenanceTask(payload, token);
        showToast("Task created.");
      }
      setTaskModalOpen(false);
      await loadTasks();
    } catch (err) {
      setTaskError(err?.response?.data?.detail || "Unable to save task.");
    } finally {
      setTaskSaving(false);
    }
  };

  const completeTask = async () => {
    if (!completeTarget?.id) return;
    setCompleteSaving(true);
    setCompleteError("");
    try {
      const note = normalizeText(completeNote);
      await completeMaintenanceTask(completeTarget.id, note ? { note } : {}, token);
      setCompleteTarget(null);
      setCompleteNote("");
      showToast("Saved. Task marked completed.");
      await loadTasks();
    } catch (err) {
      setCompleteError(err?.response?.data?.detail || "Unable to mark task done.");
    } finally {
      setCompleteSaving(false);
    }
  };

  const addDefaults = async () => {
    if (!canManage) return;
    try {
      const data = await seedMaintenanceDefaults(token);
      const inserted = Number(data?.inserted_count || 0);
      showToast(inserted ? `${inserted} default tasks added.` : "No new defaults were needed.");
      await loadTasks();
    } catch (err) {
      setTasksError(err?.response?.data?.detail || "Unable to add defaults.");
    }
  };

  const saveReference = async () => {
    if (!canManage) return;
    const title = String(referenceForm.title || "").trim();
    if (!title) {
      setComplianceError("Reference title is required.");
      return;
    }
    setReferenceSaving(true);
    setComplianceError("");
    const payload = {
      title,
      url: normalizeText(referenceForm.url),
      note: normalizeText(referenceForm.note),
    };
    try {
      if (referenceEditId) {
        await updateComplianceReference(referenceEditId, payload, token);
        showToast("Compliance reference updated.");
      } else {
        await createComplianceReference(payload, token);
        showToast("Compliance reference added.");
      }
      setReferenceEditId(null);
      setReferenceForm(EMPTY_REFERENCE_FORM);
      await loadCompliance();
    } catch (err) {
      setComplianceError(err?.response?.data?.detail || "Unable to save compliance reference.");
    } finally {
      setReferenceSaving(false);
    }
  };

  const logsHasPrevious = logsData.page > 1;
  const logsHasNext = logsData.page * logsData.page_size < logsData.total;

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="manageSectionShell">
      <div className="manageSectionWide manage-section-wide manage-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">Maintenance &amp; Cleaning</h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <ReportHelpCard
              title="Help Guide: Maintenance & Cleaning"
              subtitle="Assign, track, and verify routine sanitation and maintenance."
              sections={HELP_SECTIONS}
              troubleshooting={TROUBLESHOOTING_ITEMS}
            />

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">Task Board</h2>
                  <p className="text-sm text-slate-600">
                    {isStaffScopedView
                      ? "Track and complete your assigned cleaning and maintenance tasks."
                      : "Track cleaning and maintenance completion across staff."}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setComplianceOpen(true)}
                    className="rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Compliance References
                  </button>
                  {canManage && (
                    <>
                      <button
                        type="button"
                        onClick={addDefaults}
                        className="rounded-full border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Add Defaults
                      </button>
                      <button
                        type="button"
                        onClick={openTaskModalForCreate}
                        className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                      >
                        Add Task
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Rules vary by jurisdiction. Confirm requirements with your local health authority or state board.
              </div>
            </div>

            {tasksError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {tasksError}
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 lg:p-6 space-y-4">
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
                <div className="lg:col-span-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Search</label>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                    placeholder="Search title or description"
                    className="mt-1 h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10"
                  />
                </div>
                {canManage && (
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Assigned Staff</label>
                    <select
                      value={filters.assigned_staff_id}
                      onChange={(event) => setFilters((prev) => ({ ...prev, assigned_staff_id: event.target.value }))}
                      className="mt-1 h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10"
                    >
                      <option value="">All</option>
                      {staffOptions.map((staff) => (
                        <option key={staff.id} value={staff.id}>
                          {staff.display_name || `Staff #${staff.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <span>Status</span>
                    <InfoTooltip text={TOOLTIPS.status} label="Status Colors" />
                  </label>
                  <select
                    value={filters.status_color}
                    onChange={(event) => setFilters((prev) => ({ ...prev, status_color: event.target.value }))}
                    className="mt-1 h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10"
                  >
                    <option value="">All</option>
                    <option value="RED">Red</option>
                    <option value="YELLOW">Yellow</option>
                    <option value="GREEN">Green</option>
                  </select>
                </div>
                <div>
                  <label className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <span>Frequency</span>
                    <InfoTooltip text={TOOLTIPS.frequency} label="Frequency" />
                  </label>
                  <select
                    value={filters.frequency_type}
                    onChange={(event) => setFilters((prev) => ({ ...prev, frequency_type: event.target.value }))}
                    className="mt-1 h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10"
                  >
                    <option value="">All</option>
                    {FREQUENCY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_auto]">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Category</label>
                  <select
                    value={filters.category}
                    onChange={(event) => setFilters((prev) => ({ ...prev, category: event.target.value }))}
                    className="mt-1 h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none transition focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10"
                  >
                    <option value="">All</option>
                    {categoryOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 lg:self-end">
                  <input
                    type="checkbox"
                    checked={Boolean(filters.show_inactive)}
                    onChange={(event) => setFilters((prev) => ({ ...prev, show_inactive: event.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  Show inactive tasks
                </label>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
              <table className="w-full table-fixed text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                  <tr>
                    <th className="px-3 py-2 text-left w-28">Status</th>
                    <th className="px-3 py-2 text-left w-64">Task</th>
                    <th className="px-3 py-2 text-left w-40">Frequency</th>
                    <th className="px-3 py-2 text-left w-44">Assigned Staff</th>
                    <th className="px-3 py-2 text-left w-48">Last Done</th>
                    <th className="px-3 py-2 text-left w-40">Last Done By</th>
                    <th className="px-3 py-2 text-left w-48">Next Due</th>
                    <th className="px-3 py-2 text-right w-56">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {loadingTasks && <tr><td className="px-3 py-4 text-slate-500" colSpan={8}>Loading maintenance tasks...</td></tr>}
                  {!loadingTasks && tasks.length === 0 && <tr><td className="px-3 py-4 text-slate-500" colSpan={8}>No tasks found for current filters.</td></tr>}
                  {!loadingTasks && tasks.map((task) => {
                    const statusKey = String(task?.status_color || "RED").toUpperCase();
                    return (
                      <tr key={task.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2.5 align-top">
                          <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_BADGE_STYLES[statusKey] || STATUS_BADGE_STYLES.RED}`}>
                            {STATUS_LABELS[statusKey] || "Overdue"}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 align-top">
                          <p className="font-semibold text-slate-900">{task.title}</p>
                          {task.description ? <p className="text-xs text-slate-600 mt-1">{task.description}</p> : null}
                          {task.category ? <span className="mt-1 inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{task.category}</span> : null}
                          {!task.is_active ? <span className="ml-1 inline-flex rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Inactive</span> : null}
                        </td>
                        <td className="px-3 py-2.5 text-slate-700 align-top">{frequencyLabel(task)}</td>
                        <td className="px-3 py-2.5 text-slate-700 align-top">{task.assigned_staff_name || "--"}</td>
                        <td className="px-3 py-2.5 text-slate-700 align-top">{displayDateTime(task.last_completed_at_local, task.last_completed_at_utc, salonTimezone)}</td>
                        <td className="px-3 py-2.5 text-slate-700 align-top">{task.last_completed_by_username || "--"}</td>
                        <td className="px-3 py-2.5 text-slate-700 align-top">{displayDateTime(task.next_due_at_local, task.next_due_at_utc, salonTimezone)}</td>
                        <td className="px-3 py-2.5 text-right align-top">
                          <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
                            {task.can_mark_done && task.is_active ? (
                              <button type="button" onClick={() => { setCompleteTarget(task); setCompleteNote(""); setCompleteError(""); }} className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white hover:bg-slate-800">
                                Mark Done
                              </button>
                            ) : <span className="text-slate-400">No mark access</span>}
                            <button type="button" onClick={() => { setLogsTask(task); setLogsData((prev) => ({ ...prev, page: 1 })); setLogsOpen(true); }} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                              View Logs
                            </button>
                            {canManage && <button type="button" onClick={() => openTaskModalForEdit(task)} className="text-xs font-semibold text-slate-700 hover:text-slate-900">Edit</button>}
                            {canManage && task.is_active && <button type="button" onClick={() => setDeactivateTarget(task)} className="text-xs font-semibold text-rose-600 hover:text-rose-700">Deactivate</button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <SBINModal isOpen={taskModalOpen} onClose={() => !taskSaving && setTaskModalOpen(false)} title={editingTask ? "Edit Task" : "Add Task"} size="lg" fitContent={true}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Task Title</label>
              <input type="text" value={taskForm.title} onChange={(event) => setTaskForm((prev) => ({ ...prev, title: event.target.value }))} className="mt-1 h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Description</label>
              <textarea rows={3} value={taskForm.description} onChange={(event) => setTaskForm((prev) => ({ ...prev, description: event.target.value }))} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Category</label>
              <input type="text" value={taskForm.category} onChange={(event) => setTaskForm((prev) => ({ ...prev, category: event.target.value }))} className="mt-1 h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10" />
            </div>
            <div>
              <label className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <span>Frequency</span>
                <InfoTooltip text={TOOLTIPS.frequency} label="Frequency" />
              </label>
              <select value={taskForm.frequency_type} onChange={(event) => setTaskForm((prev) => ({ ...prev, frequency_type: event.target.value, frequency_days: event.target.value === "CUSTOM_DAYS" ? prev.frequency_days : "" }))} className="mt-1 h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10">
                {FREQUENCY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            {taskForm.frequency_type === "CUSTOM_DAYS" && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Frequency Days</label>
                <input type="number" min="1" value={taskForm.frequency_days} onChange={(event) => setTaskForm((prev) => ({ ...prev, frequency_days: event.target.value }))} className="mt-1 h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10" />
              </div>
            )}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Assigned Staff</label>
              <select value={taskForm.assigned_staff_id} onChange={(event) => setTaskForm((prev) => ({ ...prev, assigned_staff_id: event.target.value }))} className="mt-1 h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10">
                <option value="">Unassigned</option>
                {staffOptions.map((staff) => <option key={staff.id} value={staff.id}>{staff.display_name || `Staff #${staff.id}`}</option>)}
              </select>
            </div>
            {editingTask && (
              <label className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                <input type="checkbox" checked={Boolean(taskForm.is_active)} onChange={(event) => setTaskForm((prev) => ({ ...prev, is_active: event.target.checked }))} className="h-4 w-4 rounded border-slate-300" />
                Active Task
              </label>
            )}
          </div>
          {taskError && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{taskError}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => !taskSaving && setTaskModalOpen(false)} className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" disabled={taskSaving}>Cancel</button>
            <button type="button" onClick={saveTask} className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60" disabled={taskSaving}>{taskSaving ? "Saving..." : "Save Task"}</button>
          </div>
        </div>
      </SBINModal>
      <SBINModal isOpen={Boolean(completeTarget)} onClose={() => !completeSaving && setCompleteTarget(null)} title="Mark Task Done" size="md" fitContent={true}>
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">{completeTarget?.title || "Task"}</p>
            <p className="mt-1">Completion logs timestamp and username automatically.</p>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Completion Note (Optional)</label>
            <textarea rows={3} value={completeNote} onChange={(event) => setCompleteNote(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10" />
          </div>
          {completeError && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{completeError}</div>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => !completeSaving && setCompleteTarget(null)} className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" disabled={completeSaving}>Cancel</button>
            <button type="button" onClick={completeTask} className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60" disabled={completeSaving}>{completeSaving ? "Saving..." : "Mark Done"}</button>
          </div>
        </div>
      </SBINModal>

      <SBINModal isOpen={logsOpen} onClose={() => setLogsOpen(false)} title="Task Completion Logs" size="lg" fitContent={false}>
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            <p className="font-semibold text-slate-900">{logsTask?.title || "Task"}</p>
            <p className="mt-1 text-xs text-slate-600">Displayed in salon timezone: {salonTimezone}</p>
          </div>
          {logsLoading ? (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-5 text-sm text-slate-500">Loading logs...</div>
          ) : logsData.logs.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-5 text-sm text-slate-500">No completion logs yet.</div>
          ) : (
            <div className="space-y-3">
              {logsData.logs.map((log) => (
                <div key={log.id} className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <p className="text-sm font-semibold text-slate-900">{log.completed_at_local || "--"}</p>
                  <p className="text-sm text-slate-700">Completed by: <span className="font-medium">{log.completed_by_username || "--"}</span></p>
                  {log.note ? <p className="text-sm text-slate-600 mt-1">{log.note}</p> : null}
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <button type="button" disabled={!logsHasPrevious || logsLoading} onClick={() => setLogsData((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))} className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Previous</button>
            <span className="text-xs text-slate-500">Page {logsData.page}</span>
            <button type="button" disabled={!logsHasNext || logsLoading} onClick={() => setLogsData((prev) => ({ ...prev, page: prev.page + 1 }))} className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Next</button>
          </div>
        </div>
      </SBINModal>

      <SBINModal isOpen={complianceOpen} onClose={() => { setComplianceOpen(false); setReferenceEditId(null); setReferenceForm(EMPTY_REFERENCE_FORM); }} title="Compliance References" size="lg" fitContent={false}>
        <div className="space-y-4">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{TOOLTIPS.compliance}</div>
          <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
            <h3 className="text-base font-semibold text-slate-900">Saved References</h3>
            {complianceLoading ? <p className="text-sm text-slate-500">Loading references...</p> : null}
            {!complianceLoading && complianceRefs.length === 0 ? <p className="text-sm text-slate-500">No references saved yet.</p> : null}
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {complianceRefs.map((item) => (
                <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-sm font-semibold text-slate-900">{item.title}</p>
                  {item.url ? <a href={item.url} target="_blank" rel="noreferrer" className="text-xs font-medium text-sky-700 hover:text-sky-800 break-all">{item.url}</a> : null}
                  {item.note ? <p className="text-xs text-slate-600 mt-1">{item.note}</p> : null}
                  {canManage && (
                    <div className="mt-2 flex gap-2">
                      <button type="button" onClick={() => { setReferenceEditId(item.id); setReferenceForm({ title: item.title || "", url: item.url || "", note: item.note || "" }); }} className="text-xs font-semibold text-slate-700 hover:text-slate-900">Edit</button>
                      <button type="button" onClick={() => setReferenceDeleteTarget(item)} className="text-xs font-semibold text-rose-600 hover:text-rose-700">Delete</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          {canManage && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
              <h3 className="text-base font-semibold text-slate-900">{referenceEditId ? "Edit Reference" : "Add Reference"}</h3>
              <input type="text" value={referenceForm.title} onChange={(event) => setReferenceForm((prev) => ({ ...prev, title: event.target.value }))} placeholder="Title" className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10" />
              <input type="url" value={referenceForm.url} onChange={(event) => setReferenceForm((prev) => ({ ...prev, url: event.target.value }))} placeholder="URL (optional)" className="h-10 w-full rounded-xl border border-slate-300 px-3 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10" />
              <textarea rows={3} value={referenceForm.note} onChange={(event) => setReferenceForm((prev) => ({ ...prev, note: event.target.value }))} placeholder="Note (optional)" className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-900/10" />
              {complianceError && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{complianceError}</div>}
              <div className="flex justify-end gap-2">
                {referenceEditId ? <button type="button" onClick={() => { setReferenceEditId(null); setReferenceForm(EMPTY_REFERENCE_FORM); }} className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel Edit</button> : null}
                <button type="button" onClick={saveReference} className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60" disabled={referenceSaving}>{referenceSaving ? "Saving..." : referenceEditId ? "Update Reference" : "Add Reference"}</button>
              </div>
            </div>
          )}
        </div>
      </SBINModal>

      <ConfirmModal
        open={Boolean(deactivateTarget)}
        title="Deactivate Task"
        message="This task will be hidden from active views."
        confirmText={deactivateSaving ? "Deactivating..." : "Deactivate"}
        cancelText="Cancel"
        onClose={() => !deactivateSaving && setDeactivateTarget(null)}
        onConfirm={async () => {
          if (!deactivateTarget?.id) return;
          setDeactivateSaving(true);
          try {
            await deactivateMaintenanceTask(deactivateTarget.id, token);
            setDeactivateTarget(null);
            showToast("Task deactivated.");
            await loadTasks();
          } catch (err) {
            setTasksError(err?.response?.data?.detail || "Unable to deactivate task.");
          } finally {
            setDeactivateSaving(false);
          }
        }}
        isBusy={deactivateSaving}
      />

      <ConfirmModal
        open={Boolean(referenceDeleteTarget)}
        title="Delete Reference"
        message="Are you sure you want to delete this compliance reference?"
        confirmText={referenceDeleteSaving ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        onClose={() => !referenceDeleteSaving && setReferenceDeleteTarget(null)}
        onConfirm={async () => {
          if (!referenceDeleteTarget?.id) return;
          setReferenceDeleteSaving(true);
          try {
            await deleteComplianceReference(referenceDeleteTarget.id, token);
            setReferenceDeleteTarget(null);
            showToast("Compliance reference removed.");
            await loadCompliance();
          } catch (err) {
            setComplianceError(err?.response?.data?.detail || "Unable to delete compliance reference.");
          } finally {
            setReferenceDeleteSaving(false);
          }
        }}
        isBusy={referenceDeleteSaving}
      />

      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 shadow-lg">
          {toastMessage}
        </div>
      )}
    </div>
  );
}
